import React, { useState, useEffect, useRef } from 'react';
import { tagService } from '../services/api';
import { X, Search, Plus, Tag, Loader2 } from 'lucide-react';
import { Badge } from './ui/badge';

const TagSelector = ({ selectedTags = [], onTagsChange, allowCreate = true }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [createMode, setCreateMode] = useState(false);

  const inputRef = useRef(null);
  const timeoutRef = useRef(null);

  // Buscar tags quando o termo de busca muda
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (searchTerm.trim().length > 0) {
      timeoutRef.current = setTimeout(async () => {
        try {
          setLoading(true);
          const response = await tagService.searchTags(searchTerm);

          if (response.success) {
            // Filtrar tags já selecionadas
            const filteredTags = response.data.tags.filter(
              tag => !selectedTags.find(selected => selected.id === tag.id)
            );
            setSuggestions(filteredTags);

            // Verificar se o termo digitado é uma tag existente
            const exactMatch = response.data.tags.find(
              tag => tag.name.toLowerCase() === searchTerm.toLowerCase()
            );
            setCreateMode(allowCreate && !exactMatch && searchTerm.trim().length >= 2);
          }
        } catch (error) {
          console.error('Erro ao buscar tags:', error);
          setSuggestions([]);
        } finally {
          setLoading(false);
        }
      }, 300);
    } else {
      setSuggestions([]);
      setCreateMode(false);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [searchTerm, selectedTags, allowCreate]);

  const handleTagSelect = (tag) => {
    if (!selectedTags.find(selected => selected.id === tag.id)) {
      onTagsChange([...selectedTags, tag]);
    }
    setSearchTerm('');
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const handleTagRemove = (tagToRemove) => {
    onTagsChange(selectedTags.filter(tag => tag.id !== tagToRemove.id));
  };

  const handleCreateTag = async () => {
    if (!searchTerm.trim() || !allowCreate) return;

    try {
      setLoading(true);
      const response = await tagService.createTag({
        name: searchTerm.trim(),
        color: getRandomColor()
      });

      if (response.success) {
        handleTagSelect(response.data.tag);
      } else {
        alert('Erro ao criar tag: ' + response.message);
      }
    } catch (error) {
      console.error('Erro ao criar tag:', error);
      alert('Erro ao criar tag');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      if (suggestions.length > 0) {
        handleTagSelect(suggestions[0]);
      } else if (createMode) {
        handleCreateTag();
      }
    } else if (e.key === 'Backspace' && searchTerm === '' && selectedTags.length > 0) {
      // Remover última tag se input estiver vazio
      handleTagRemove(selectedTags[selectedTags.length - 1]);
    }
  };

  const getRandomColor = () => {
    const colors = [
      '#3B82F6', '#EF4444', '#10B981', '#F59E0B',
      '#8B5CF6', '#06B6D4', '#84CC16', '#EC4899'
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  return (
    <div className="w-full space-y-3">
      {/* Selected Tags */}
      <div className="flex flex-wrap gap-2 min-h-8">
        {selectedTags.map((tag) => (
          <Badge
            key={tag.id}
            className="pl-2 pr-1 py-1 flex items-center gap-1 text-white border-0 text-sm font-normal"
            style={{ backgroundColor: tag.color || '#6B7280' }}
          >
            {tag.name}
            <button
              type="button"
              onClick={() => handleTagRemove(tag)}
              className="p-0.5 hover:bg-black/20 rounded-full transition-colors ml-1"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>

      {/* Input de busca */}
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Search className="h-4 w-4" />
          )}
        </div>

        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          onKeyDown={handleKeyDown}
          placeholder={selectedTags.length === 0 ? "Buscar ou criar tags..." : "Adicionar mais tags..."}
          className="w-full h-10 pl-9 pr-4 rounded-md bg-white/5 border border-white/10 text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-church-green/20 focus:border-church-green/50 text-sm"
        />

        {/* Sugestões Dropdown */}
        {showSuggestions && searchTerm.trim().length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-[#0a0a0a] border border-white/10 rounded-md shadow-xl overflow-hidden animate-in fade-in zoom-in-95 duration-100">
            {suggestions.map((tag) => (
              <button
                key={tag.id}
                type="button"
                onClick={() => handleTagSelect(tag)}
                className="w-full text-left px-4 py-2 hover:bg-white/5 flex items-center gap-2 text-white text-sm transition-colors"
              >
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: tag.color || '#6B7280' }}
                />
                {tag.name}
              </button>
            ))}

            {createMode && (
              <button
                type="button"
                onClick={handleCreateTag}
                className="w-full text-left px-4 py-2 hover:bg-church-green/10 flex items-center gap-2 text-church-green text-sm border-t border-white/5"
              >
                <Plus className="h-4 w-4" />
                Criar tag <span className="font-semibold text-white">"{searchTerm}"</span>
              </button>
            )}

            {suggestions.length === 0 && !createMode && !loading && (
              <div className="px-4 py-3 text-sm text-gray-400 text-center">
                Nenhuma tag encontrada
                {allowCreate && <span className="block text-xs mt-1 text-gray-600">Continue digitando para criar</span>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TagSelector;